/** 學生側欄動態化:登入檢查、學號/姓名/學分、學習總覽課程(含課程第三層子選單)、登出。
 *  自 public/js/sidebar.js 移植。
 */
import avatarUrl from '../assets/avatar.jpg';
import iconsUrl from '../assets/icons.svg';
import logoUrl from '../assets/logo-mdbs.svg';
import { request, requireAuth } from './api';
import { hide } from './loading';
import type { Me, SidebarCourse, StudentSummary } from './types';

// 這些資源是由 JS 產生的 HTML 引用的,必須以 import 交給建置工具處理;
// 寫死字串會在建置後 404(資源會被雜湊改名,且不在輸出目錄)。
const ICONS = iconsUrl;

export type SidebarActive = 'info' | 'homework' | 'records';

export interface SidebarOptions {
  courseId?: string | number | null;
  active?: SidebarActive;
}

export interface SidebarResult {
  me: Me;
  summary: StudentSummary | null;
  courses: SidebarCourse[] | null;
}

const icon = (id: string, size: number, cls: string) =>
  `<svg width="${size}" height="${size}" class="${cls}"><use href="${ICONS}#${id}"></use></svg>`;

const COLLAPSE_KEY = 'ekm.sidebar.collapsed';

/** 側欄收合鈕:切換 .ekm-sidebar--collapsed(只留圖示),狀態記在 localStorage 跨頁沿用。 */
function initCollapse(host: HTMLElement): void {
  const btn = host.querySelector<HTMLElement>('.bt-menu');
  if (!btn) return;
  let collapsed = false;
  try {
    collapsed = localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch { /* 隱私模式讀不到就當作展開 */ }

  const apply = () => {
    host.classList.toggle('ekm-sidebar--collapsed', collapsed);
    btn.setAttribute('aria-label', collapsed ? '展開' : '收合');
    btn.setAttribute('aria-expanded', String(!collapsed));
  };
  apply();
  btn.addEventListener('click', () => {
    collapsed = !collapsed;
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch { /* 隱私模式忽略 */ }
    apply();
  });
}

/** 課程頁沒有靜態側欄骨架,由 JS 生成(結構同 index.html 的 .ekm-sidebar) */
function buildSkeleton(): string {
  return '<div class="ekm-sidebar__logo">'
    + '<div class="ekm-sidebar__brand">'
    + `<img src="${logoUrl}" alt="MDBS" class="logo">`
    + '<span>教學EKM管理系統</span>'
    + '</div>'
    + '<button aria-label="收合" class="bt-menu"></button>'
    + '</div>'
    + '<div class="ekm-sidebar__user">'
    + `<img src="${avatarUrl}" alt="頭像">`
    + '<div class="ekm-sidebar__user-info"></div>'
    + '</div>'
    + '<nav class="ekm-sidebar__nav">'
    + '<p class="ekm-sidebar__section-label">主頁</p>'
    + `<a href="index.html" class="ekm-sidebar__item">${icon('ic-cal', 24, 'sx12')}<span>行事曆</span></a>`
    + `<div class="ekm-sidebar__item">${icon('ic-lesson', 24, 'ic-6c61e1')}<span>學習總覽</span>${icon('ic-up', 16, 'ic-8c8c8c')}</div>`
    + '<ul class="ekm-sidebar__submenu"></ul>'
    + `<a href="homework.html" class="ekm-sidebar__item">${icon('ic-homework', 24, 'ic-6c61e1')}<span>我的作業</span></a>`
    + `<a href="homework.html" class="ekm-sidebar__item">${icon('ic-lessoninfo', 24, 'ic-6c61e1')}<span>課程資訊</span></a>`
    + `<a href="ai.html" class="ekm-sidebar__item">${icon('ic-spark', 24, 'ic-6c61e1')}<span>AI 選課</span></a>`
    + `<a href="performance.html" class="ekm-sidebar__item">${icon('ic-data', 24, 'ic-6c61e1')}<span>我的績效</span></a>`
    + '<p class="ekm-sidebar__section-label">設定</p>'
    + `<a href="index.html" class="ekm-sidebar__item logout">${icon('ic-logout', 24, 'ic-D97706')}<span>登出</span></a>`
    + '</nav>';
}

export async function init(opts: SidebarOptions = {}): Promise<SidebarResult> {
  try {
    const me = await requireAuth();

    // 課程頁的側欄容器是空的 → 先生成骨架;主頁已有靜態骨架則跳過
    const host = document.querySelector<HTMLElement>('.ekm-sidebar');
    if (host && !host.innerHTML.trim()) host.innerHTML = buildSkeleton();
    if (host) initCollapse(host);

    const headerTitle = document.querySelector<HTMLElement>('.ekm-header__title');
    if (headerTitle && headerTitle.textContent?.includes('歡迎回來')) {
      headerTitle.textContent = `${me.name},歡迎回來!`;
    }
    const headerUser = document.querySelector<HTMLElement>('.ekm-header__user span');
    if (headerUser) headerUser.textContent = me.name;

    let summary: StudentSummary | null = null;
    let courses: SidebarCourse[] | null = null;

    if (me.role === 'student') {
      [summary, courses] = await Promise.all([
        request<StudentSummary>('/api/student/summary'),
        request<SidebarCourse[]>('/api/student/courses'),
      ]);

      const info = document.querySelector<HTMLElement>('.ekm-sidebar__user-info');
      if (info) {
        info.innerHTML =
          `<div>學號:${summary.account} ${summary.name}</div>`
          + `<div>目前學分進度:<span class="ekm-sidebar__user-credits">${summary.earnedCredits}`
          + `</span> / ${summary.gradCredits}</div>`;
      }

      const curId = opts.courseId ? Number(opts.courseId) : null;
      const l3 = (key: SidebarActive, label: string, href: string) =>
        `<li><a${opts.active === key ? ' class="is-active"' : ''} href="${href}">${label}</a></li>`;

      const submenu = document.querySelector<HTMLElement>('.ekm-sidebar__submenu');
      if (submenu) {
        submenu.innerHTML = courses.map((c) => {
          const cur = c.id === curId;
          let html = `<li${cur ? ' class="is-open"' : ''}><a href="course.html?id=${c.id}">${c.name}</a>`;
          if (cur) {
            html += '<ul class="ekm-sidebar__submenu-l3">'
              + l3('info', '課程資訊', `course.html?id=${c.id}`)
              + l3('homework', '我的作業', 'homework.html')
              + l3('records', '測驗紀錄', `course-records.html?id=${c.id}`)
              + '</ul>';
          }
          return `${html}</li>`;
        }).join('');
      }

      if (curId) {
        document.querySelectorAll<HTMLElement>('.ekm-sidebar__item').forEach((item) => {
          const span = item.querySelector('span');
          if (span && span.textContent === '學習總覽') item.classList.add('is-active');
        });
      }

      // 修正切版連結:行事曆 → index.html;課程資訊 → 第一門課
      const first = courses[0];
      document.querySelectorAll<HTMLAnchorElement>('.ekm-sidebar__nav a').forEach((a) => {
        const label = a.querySelector('span')?.textContent ?? '';
        if (label === '行事曆') a.setAttribute('href', 'index.html');
        if (label === '課程資訊' && first) a.setAttribute('href', `course.html?id=${first.id}`);
      });
    }

    const logout = document.querySelector<HTMLElement>('.ekm-sidebar__item.logout');
    logout?.addEventListener('click', (e) => {
      e.preventDefault();
      void request('/api/auth/logout', { method: 'POST' }).then(() => {
        window.location.href = 'login.html';
      });
    });

    return { me, summary, courses };
  } finally {
    hide();
  }
}
